import { getPreferredPhone, useGetAllContacts, useGetContact } from "@api/contactService";
import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import { useGetEvent } from "@api/eventService";
import { getFrequencyName, useGetDonation } from "@api/donationService";
import { CheckPermission } from "@components/CheckPermission";
import { RecordDonationModal } from "@components/RecordDonationModal";

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').toUpperCase() : '';

    return (
    <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1.5}
        pt={0}
    >
        <Box sx={{flex: 1}}>
            <Typography fontSize={'md'} fontWeight={'bold'}>Donation Info</Typography>
            {/* {data.location && <Typography fontSize={'sm'}>{data.location}</Typography>} */}
        </Box>
    </Stack>
)
};

const dataToDisplay/*: readonly ShowData[]*/ = [
    {
        label: 'Donor',
        conditional: object => !!object.donor,
        content: (object) => (
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                    {!!object.anonymous && (<>
                        Anonymous
                        <CheckPermission permissions={'view donations'}>
                            {` (${object.donor?.name})`}
                        </CheckPermission>
                    </>)}

                    <ObjectPreview to={`/${object.donor_type == 'App\\Models\\Contact' ? 'contacts' : 'businesses'}/${object.donor.id}`} object={object.donor}>
                        {!object.anonymous && (<>{object.donor?.name ?? 'N/A'}</>)}
                    </ObjectPreview>
                </Typography>
        ),
    },
    {
        label: 'Type',
        conditional: object => !!object.type,
        content: 'type',
    },
    {
        label: 'Frequency',
        conditional: object => object?.type === 'Recurring',
        content: (object) => (<>
            {object.frequency}
            {!!object.num_donations && (<>
            {' for '}
            {object.num_donations} {getFrequencyName(object.frequency)}
            </>)}
        </>),
    },
    {
        label: 'Fund',
        conditional: object => !!object.fund,
        content: (object) => object.fund.name,
    },
    {
        label: 'Appeal',
        conditional: object => !!object.appeal,
        content: (object) => object.appeal.name,
    },
    {
        label: 'Notes',
        conditional: object => object?.notes && object.notes.trim().length > 0,
        content: 'notes',
    },
];

const AddDonationButton = () => {
    const data = useContext(ObjectContext) ?? {};
    if(data.type !== 'Recurring') {
        return null;
    }

    return (
        <CheckPermission permissions={'edit donations'}>
            <RecordDonationModal donation={data}>
            {(open, setOpen) => (
                <Tooltip title="Record New Donation">
                    <IconButton
                        size="sm"
                        color="neutral"
                        onClick={()=>setOpen(true)}
                        // disabled={disabled}
                        sx={{
                        bgcolor: 'background.level1',
                        '--variant-outlinedHoverBg': '#FFFFFF6B',
                        '--variant-outlinedActiveBg': '#FFFFFFB8',// theme.palette.background.level1
                        '--IconButton-size': '1.65rem',
                        'svg': {
                            width: '1.05rem',
                            height: '1.05rem',
                        }
                        }}
                    >
                        <AddRoundedIcon/>
                    </IconButton>
                </Tooltip>
            )}
            </RecordDonationModal>
        </CheckPermission>
    );
};

export default function DonationShow() {
    return (
        <ObjectShow
            query={useGetDonation}
            type={'donation'}
            permissionType={'donations'}
            title={'Donations'}
            subtitleKey={'name'}
            color={'default'}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfo
                    title={'Donation Info'}
                    tint={'default'}
                    fields={dataToDisplay}
                    cardHeader={<CardHeader/>}
                />
                {/* <ObjectRelationship type={'notes'} column={'description'} title={'Description'} /> */}
            </ObjectColumn>

            <ObjectColumn>
                {/* <ObjectRelationship type={'donation_entries'} /> */}
                <ObjectRelationship
                    type={'donation__donation_entries'}
                    column="donation_entries"
                    sx={{
                        opacity: '1 !important',
                    }}
                    action={<AddDonationButton/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                {/* Empty */}
            </ObjectColumn>
        </ObjectShow>
    )
}
